import React, { useEffect, useState, useCallback } from "react";
import { Activity, RefreshCw, ShieldCheck, ServerCog, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

const STATUS_COLORS = {
  queued: "text-sky-300", verifying: "text-amber-300", pending: "text-amber-300",
  confirmed: "text-emerald-400", already_processed: "text-emerald-400/70",
  failed: "text-rose-400", rejected: "text-rose-400", manual_review: "text-orange-300",
};
const LEVEL = { critical: "border-rose-500/40 bg-rose-500/10 text-rose-300", warning: "border-amber-500/40 bg-amber-500/10 text-amber-300", info: "border-sky-500/40 bg-sky-500/10 text-sky-300" };

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-white/10 bg-white/[0.03] p-5 ${className}`}>{children}</div>;
}

export default function AdminDepositVerify() {
  const [health, setHealth] = useState(null);
  const [rpc, setRpc] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, r, j] = await Promise.all([
        api.get("/admin/system-health"),
        api.get("/admin/bsc-verifier-status"),
        api.get(`/admin/deposit-verifications${filter ? `?status=${filter}` : ""}`),
      ]);
      setHealth(h.data); setRpc(r.data); setJobs(j.data.jobs || []);
    } catch (e) { /* handled by interceptor */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const counts = rpc?.job_counts || {};
  const overall = health?.status || "ok";

  return (
    <div className="space-y-6" data-testid="admin-deposit-verify">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-white flex items-center gap-2"><ServerCog className="text-emerald-400" size={22} /> Deposit Verifications & Health</h1>
          <p className="text-sm text-[var(--text-dim)]">Live BSC/BEP20 verification queue, RPC provider health and production alerts.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white hover:bg-white/10" data-testid="dv-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Overall status + alerts */}
      <Card className={overall === "critical" ? "border-rose-500/40" : overall === "warning" ? "border-amber-500/40" : "border-emerald-500/30"}>
        <div className="flex items-center gap-2" data-testid="dv-overall-status">
          {overall === "ok" ? <CheckCircle2 className="text-emerald-400" size={20} /> : <AlertTriangle className={overall === "critical" ? "text-rose-400" : "text-amber-400"} size={20} />}
          <span className="text-lg font-bold text-white uppercase">System status: {overall}</span>
          <span className="ml-auto text-xs text-[var(--text-dim)]">Auto-confirm: <b className={health?.deposit_verification?.auto_confirm_enabled ? "text-emerald-400" : "text-white/60"}>{health?.deposit_verification?.auto_confirm_enabled ? "ENABLED" : "OFF (manual)"}</b></span>
        </div>
        {(health?.alerts || []).length > 0 ? (
          <div className="mt-3 space-y-2">
            {health.alerts.map((a, i) => (
              <div key={i} className={`rounded-lg border px-3 py-2 text-sm ${LEVEL[a.level] || LEVEL.info}`} data-testid={`dv-alert-${a.code}`}>
                <b className="uppercase">{a.level}</b> — {a.code}{a.value != null ? ` (${a.value})` : ""}
              </div>
            ))}
          </div>
        ) : <p className="mt-2 text-sm text-emerald-400/80">No active alerts.</p>}
      </Card>

      {/* Queue + signer + cron */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <p className="text-xs uppercase tracking-wider text-[var(--text-dim)]">Queue backlog</p>
          <p className="mt-1 text-3xl font-extrabold text-white" data-testid="dv-queue-depth">{health?.deposit_verification?.queue_backlog ?? "—"}</p>
          <p className="text-xs text-[var(--text-dim)]">queued + pending</p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wider text-[var(--text-dim)]">RPC providers up</p>
          <p className="mt-1 text-3xl font-extrabold text-white">{health?.rpc?.providers_up ?? "—"}<span className="text-base text-[var(--text-dim)]">/{health?.rpc?.providers_total ?? "—"}</span></p>
          <p className="text-xs text-[var(--text-dim)]">req. confs: {health?.rpc?.required_confirmations}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wider text-[var(--text-dim)]">Withdrawal signer</p>
          <p className="mt-1 flex items-center gap-1 text-lg font-bold text-white"><ShieldCheck size={16} className={health?.withdrawal_signer?.enabled ? "text-emerald-400" : "text-amber-400"} /> {health?.withdrawal_signer?.enabled ? "LIVE" : "MANUAL"}</p>
          <p className="text-xs text-[var(--text-dim)]">pending WD: {health?.withdrawals?.pending ?? "—"}</p>
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-wider text-[var(--text-dim)]">ROI cron</p>
          <p className="mt-1 text-lg font-bold text-white">{health?.cron?.stale ? "No recent run" : "Recent"}</p>
          <p className="text-xs text-[var(--text-dim)]">{health?.cron?.last_run_at ? new Date(health.cron.last_run_at).toLocaleString() : "—"}</p>
        </Card>
      </div>

      {/* Provider health table */}
      <Card>
        <p className="mb-3 flex items-center gap-2 text-sm font-bold text-white"><Activity size={16} className="text-emerald-400" /> RPC Provider Health</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-[var(--text-dim)]">
              <th className="py-2">Provider</th><th>OK</th><th>Fail</th><th>Timeout</th><th>Rate-limited</th><th>Avg ms</th><th>State</th>
            </tr></thead>
            <tbody>
              {(rpc?.providers || []).map((p, i) => (
                <tr key={i} className="border-t border-white/5 text-white/90" data-testid={`dv-provider-${i}`}>
                  <td className="py-2 font-mono text-xs">{p.url}</td>
                  <td className="text-emerald-400">{p.ok}</td><td className="text-rose-400">{p.fail}</td>
                  <td>{p.timeout}</td><td>{p.rate_limited}</td><td>{p.avg_latency_ms}</td>
                  <td>{p.in_cooldown ? <span className="text-amber-400">cooldown</span> : <span className="text-emerald-400">healthy</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Job status counts + filter */}
      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <p className="text-sm font-bold text-white">Verification Jobs</p>
          <div className="ml-auto flex flex-wrap gap-1.5">
            {["", "queued", "verifying", "pending", "confirmed", "failed", "rejected", "manual_review"].map((s) => (
              <button key={s || "all"} onClick={() => setFilter(s)}
                className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${filter === s ? "bg-emerald-500 text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
                data-testid={`dv-filter-${s || "all"}`}>
                {s || "all"}{s && counts[s] != null ? ` (${counts[s]})` : ""}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-[var(--text-dim)]">
              <th className="py-2">Deposit</th><th>Status</th><th>Confs</th><th>Attempts</th><th>Amount</th><th>Reason</th><th>Updated</th>
            </tr></thead>
            <tbody>
              {jobs.length === 0 ? (
                <tr><td colSpan={7} className="py-6 text-center text-[var(--text-dim)]">No verification jobs{filter ? ` (${filter})` : ""}.</td></tr>
              ) : jobs.map((j) => (
                <tr key={j.job_id} className="border-t border-white/5 text-white/90" data-testid={`dv-job-${j.dep_id}`}>
                  <td className="py-2 font-mono text-xs">{j.dep_id}</td>
                  <td className={`font-semibold ${STATUS_COLORS[j.status] || "text-white"}`}>{j.status}</td>
                  <td>{j.confirmations ?? 0}</td><td>{j.attempts ?? 0}</td>
                  <td>{j.amount ?? "—"}</td>
                  <td className="max-w-[220px] truncate text-xs text-[var(--text-dim)]" title={j.last_reason || ""}>{j.last_reason || "—"}</td>
                  <td className="text-xs text-[var(--text-dim)]">{j.updated_at ? new Date(j.updated_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
